import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'
import {
  Camera,
  MoreVertical,
  Pencil,
  Plus,
  Power,
  Search,
  Trash2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  FORM_CONTROL_FOCUS_CLASS,
  FORM_CONTROL_FOCUS_WITHIN_CLASS,
  FORM_CONTROL_SELECT_OPEN_CLASS,
  FORM_FILTER_HOVER_CLASS,
} from '@/components/ui/form-control-styles'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { SettingsDrawerHeader } from '@/components/settings/SettingsDrawerHeader'
import { ServiceCategoryIcon } from '@/components/icons/ServiceCategoryIcons'
import { ServiceCategoryFilter } from '@/components/settings/services/ServiceCategoryFilter'
import { ListPagination } from '@/components/shared/ListPagination'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import { EntityPicker } from '@/components/shared/EntityPicker'
import { ServiceItemImage } from '@/components/shared/ServiceItemPicker'
import { ConfirmDeleteDialog } from '@/components/shared'
import { useListParams } from '@/hooks/useListParams'
import { settingsApi } from '@/services/settings'
import {
  useServiceItems,
  useServiceItemsSummary,
  useServiceCategories,
  useCreateServiceItem,
  useUpdateServiceItem,
  useDeleteServiceItem,
  useTaxRates,
} from '@/hooks/useSettings'
import { toast } from '@/components/ui/toast'
import { getApiErrorMessage } from '@/services/api'
import { formatCurrency, parseCurrencyInput } from '@/lib/format'
import {
  SERVICE_CATEGORY_ORDER,
  getServiceCategoryPalette,
  type ServiceCategoryPalette,
} from '@/lib/serviceCategoryPalette'
import {
  SETTINGS_IMAGE_ACCEPT,
  validateSettingsImage,
} from '@/lib/settingsImageUpload'
import { cn } from '@/lib/utils'
import type { ServiceItem, ServiceItemRequest } from '@/types'

const NONE = '__none__'
const STATUS_ALL = '__all__'

const filterTriggerClass = cn(
  'h-10 w-full justify-between gap-2 rounded-xl border-[#cfd4dc] bg-white px-4 text-left text-sm font-normal leading-[1.4] text-[#171a20] shadow-none [&>span]:font-normal sm:w-auto sm:min-w-[110px]',
  FORM_CONTROL_SELECT_OPEN_CLASS,
  FORM_FILTER_HOVER_CLASS
)

const searchInputClass = cn(
  'h-10 rounded-xl border-[#cfd4dc] bg-white pl-10 pr-4 text-sm font-normal leading-[1.4] text-[#171a20] placeholder:text-[#475467]',
  FORM_CONTROL_FOCUS_CLASS,
  FORM_FILTER_HOVER_CLASS
)

const sheetInputClass = cn(
  'h-11 rounded-xl border-transparent bg-[#f4f5f8] px-4 text-sm font-normal leading-[1.4] text-[#171a20] placeholder:text-[#98a2b3]',
  FORM_CONTROL_FOCUS_CLASS
)

const sheetTextareaClass = cn(
  'min-h-[88px] rounded-xl border-transparent bg-[#f4f5f8] px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] placeholder:text-[#98a2b3]',
  FORM_CONTROL_FOCUS_CLASS
)

const sheetSelectClass = cn(
  'h-11 rounded-xl border-transparent bg-[#f4f5f8] px-4 text-sm font-normal leading-[1.4] text-[#171a20]',
  FORM_CONTROL_SELECT_OPEN_CLASS
)

const cardShadowClass =
  'shadow-[0_1px_4px_-1px_rgba(38,45,66,0.08),0_1px_8px_-1px_rgba(38,45,66,0.12)]'

const sectionCardClass =
  'overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_12px_28px_-10px_rgba(38,45,66,0.14),0_4px_10px_-6px_rgba(38,45,66,0.08)]'

interface ItemFilters extends Record<string, unknown> {
  service_category_ids?: string[]
  is_active?: boolean
}

interface FormState {
  service_category_id: string
  name: string
  description: string
  unit_price: string
  unit: string
  tax_rate_id: string
  is_active: boolean
}

type ServiceItemFormRequest = ServiceItemRequest & {
  remove_image?: boolean
}

interface ItemGroup {
  id: string
  name: string
  items: ServiceItem[]
  palette: ServiceCategoryPalette
}

const EMPTY: FormState = {
  service_category_id: '',
  name: '',
  description: '',
  unit_price: '',
  unit: '',
  tax_rate_id: '',
  is_active: true,
}

function FieldLabel({
  htmlFor,
  required,
  children,
}: {
  htmlFor?: string
  required?: boolean
  children: ReactNode
}) {
  return (
    <Label
      htmlFor={htmlFor}
      className='text-xs font-medium leading-4 text-[#3d434e]'
    >
      {children}
      {required && <span className='text-[#d63b33]'> *</span>}
    </Label>
  )
}

function categoryName(item: ServiceItem, fallback: string) {
  return item.category?.name ?? item.service_category?.name ?? fallback
}

function groupItems(items: ServiceItem[], fallback: string) {
  const groups = new Map<string, ItemGroup>()

  items.forEach(item => {
    const name = categoryName(item, fallback)
    const key = item.service_category_id ?? name
    const current = groups.get(key)

    if (current) {
      current.items.push(item)
      return
    }

    groups.set(key, {
      id: key,
      name,
      items: [item],
      palette: getServiceCategoryPalette(name),
    })
  })

  return Array.from(groups.values()).sort((a, b) => {
    const aIndex = SERVICE_CATEGORY_ORDER.indexOf(a.name)
    const bIndex = SERVICE_CATEGORY_ORDER.indexOf(b.name)

    if (aIndex === -1 && bIndex === -1) return a.name.localeCompare(b.name)
    if (aIndex === -1) return 1
    if (bIndex === -1) return -1
    return aIndex - bIndex
  })
}

function StatusPill({ active }: { active: boolean }) {
  const { t } = useTranslation('settings')

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium leading-4',
        active ? 'bg-[#D1FAE5] text-[#065F46]' : 'bg-[#eceff3] text-[#475467]'
      )}
    >
      {active ? t('common.active') : t('common.inactive')}
    </span>
  )
}

function SummaryMetric({
  label,
  value,
  valueClassName,
}: {
  label: string
  value: number | undefined
  valueClassName?: string
}) {
  return (
    <div className='rounded-2xl border border-[#e7e9ef] bg-white px-6 py-4 shadow-[0_12px_28px_-10px_rgba(38,45,66,0.14),0_4px_10px_-6px_rgba(38,45,66,0.08)]'>
      <div className='text-xs font-medium text-[#6c7285]'>{label}</div>
      <div
        className={cn(
          'mt-2 text-[24px] font-semibold leading-none text-[#171a20]',
          valueClassName
        )}
      >
        {value ?? '—'}
      </div>
    </div>
  )
}

function ItemAvatar({ item }: { item: ServiceItem }) {
  return (
    <ServiceItemImage
      src={item.image_url}
      className='size-10 rounded-xl border border-[#e7e9ef] bg-[#fff1ea]'
    />
  )
}

function ItemCard({
  item,
  updating,
  onEdit,
  onToggleActive,
  onDelete,
}: {
  item: ServiceItem
  updating: boolean
  onEdit: (item: ServiceItem) => void
  onToggleActive: (item: ServiceItem) => void
  onDelete: (item: ServiceItem) => void
}) {
  const { t } = useTranslation(['settings', 'common'])

  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-2xl border border-[#e7e9ef] bg-white p-3',
        cardShadowClass
      )}
    >
      <ItemAvatar item={item} />

      <div className='min-w-0 flex-1'>
        <p className='truncate text-sm font-medium leading-[1.4] text-[#171a20]'>
          {item.name}
        </p>
        <div className='mt-1 flex flex-wrap items-center gap-2 text-xs font-medium leading-4 text-[#6c7285]'>
          <span>{formatCurrency(item.unit_price, 'EUR', 2)}</span>
          {item.unit && (
            <>
              <span className='size-1 rounded-full bg-[#8f95a6]' />
              <span>{item.unit}</span>
            </>
          )}
          <span className='size-1 rounded-full bg-[#8f95a6]' />
          <StatusPill active={item.is_active} />
        </div>
      </div>

      <div className='ml-auto flex items-center gap-2 self-center'>
        <TruncatedTextTooltip
          content={t('common:actions.edit')}
          delayDuration={0}
          alwaysShow
          closeOnPointerDown
          className='inline-flex shrink-0'
        >
          <button
            type='button'
            className='inline-flex size-8 items-center justify-center rounded-lg border border-transparent bg-[#f4f5f8] text-[#272c36] transition-colors hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f]'
            aria-label={t('common:actions.edit')}
            onClick={() => onEdit(item)}
          >
            <Pencil className='size-4' />
          </button>
        </TruncatedTextTooltip>

        <DropdownMenu>
          <TruncatedTextTooltip
            content={t('items.actions.more')}
            delayDuration={0}
            alwaysShow
            closeOnPointerDown
            className='inline-flex shrink-0'
          >
            <DropdownMenuTrigger asChild>
              <button
                type='button'
                className='inline-flex size-8 items-center justify-center rounded-lg border border-transparent bg-[#f4f5f8] text-[#272c36] transition-colors hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f] data-[state=open]:border-[#ff5a1f] data-[state=open]:bg-[#fff1ea] data-[state=open]:text-[#ff5a1f]'
                aria-label={t('items.actions.more')}
              >
                <MoreVertical className='size-4' />
              </button>
            </DropdownMenuTrigger>
          </TruncatedTextTooltip>
          <DropdownMenuContent
            align='end'
            sideOffset={8}
            className='min-w-[200px] rounded-xl border border-[#e7e9ef] bg-white p-2 shadow-[0px_1px_4px_-1px_rgba(38,45,66,0.05),0px_1px_8px_-1px_rgba(38,45,66,0.1)]'
          >
            <DropdownMenuItem
              className='cursor-pointer gap-3 rounded-lg px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] focus:bg-[#f4f5f8] focus:text-[#171a20]'
              disabled={updating}
              onClick={() => onToggleActive(item)}
            >
              <Power className='size-4 shrink-0' />
              {item.is_active
                ? t('items.actions.deactivate')
                : t('items.actions.activate')}
            </DropdownMenuItem>
            <DropdownMenuItem
              className='cursor-pointer gap-3 rounded-lg px-4 py-3 text-sm font-normal leading-[1.4] text-[#d63b33] focus:bg-[#f4f5f8] focus:text-[#d63b33]'
              onClick={() => onDelete(item)}
            >
              <Trash2 className='size-4 shrink-0' />
              {t('common:actions.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}

function CategorySection({
  group,
  updating,
  onEdit,
  onToggleActive,
  onDelete,
}: {
  group: ItemGroup
  updating: boolean
  onEdit: (item: ServiceItem) => void
  onToggleActive: (item: ServiceItem) => void
  onDelete: (item: ServiceItem) => void
}) {
  return (
    <section className='space-y-[10px]'>
      <div
        className='flex items-center gap-2 rounded-xl px-3 py-2'
        style={{ backgroundColor: group.palette.headerBg }}
      >
        <span
          className='flex size-4 shrink-0 items-center justify-center'
          style={{
            color: group.palette.headerText,
          }}
        >
          <ServiceCategoryIcon name={group.name} className='size-4' />
        </span>
        <span
          className='truncate text-xs font-medium uppercase leading-4'
          style={{ color: group.palette.headerText }}
        >
          {group.name}
        </span>
        <span className='inline-flex min-w-5 items-center justify-center rounded-full bg-[#8f95a6] px-1.5 py-0.5 text-xs font-medium leading-4 text-white'>
          {group.items.length}
        </span>
      </div>

      <div className='grid gap-[10px] xl:grid-cols-2'>
        {group.items.map(item => (
          <ItemCard
            key={item.id}
            item={item}
            updating={updating}
            onEdit={onEdit}
            onToggleActive={onToggleActive}
            onDelete={onDelete}
          />
        ))}
      </div>
    </section>
  )
}

export function ItemsPanel() {
  const { t } = useTranslation('settings')
  const {
    page,
    setPage,
    limit,
    setLimit,
    search,
    onSearchChange,
    filters,
    setFilters,
    query,
  } = useListParams<ItemFilters>()
  const { data, isLoading, isError } = useServiceItems({
    ...query,
    paginate_by_category: true,
  })
  const summary = useServiceItemsSummary().data?.data
  const categoriesList = useServiceCategories({ limit: 100 })
  const taxRates = useTaxRates({ limit: 100 })
  const create = useCreateServiceItem()
  const update = useUpdateServiceItem()
  const remove = useDeleteServiceItem()

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<ServiceItem | null>(null)
  const [form, setForm] = useState<FormState>(EMPTY)
  const [formCategoryLabel, setFormCategoryLabel] = useState<string>()
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [toDelete, setToDelete] = useState<ServiceItem | null>(null)
  const [image, setImage] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [imageRemoved, setImageRemoved] = useState(false)
  const [dialogPortalContainer, setDialogPortalContainer] =
    useState<HTMLElement | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const dialogContentRef = useRef<HTMLDivElement>(null)

  const rows = useMemo(() => data?.data ?? [], [data])
  const categoryOptions = useMemo(
    () => categoriesList.data?.data ?? [],
    [categoriesList.data]
  )
  const groups = useMemo(
    () => groupItems(rows, t('items.uncategorized')),
    [rows, t]
  )
  const set = (patch: Partial<FormState>) =>
    setForm(value => ({ ...value, ...patch }))

  const statusValue =
    filters.is_active === undefined
      ? STATUS_ALL
      : filters.is_active
        ? 'active'
        : 'inactive'

  useEffect(() => {
    if (!open) return
    setDialogPortalContainer(dialogContentRef.current)
  }, [open])

  function openCreate() {
    setEditing(null)
    setForm(EMPTY)
    setFormCategoryLabel(undefined)
    setErrors({})
    setImage(null)
    setImagePreview(null)
    setImageRemoved(false)
    setOpen(true)
  }

  function openEdit(item: ServiceItem) {
    setEditing(item)
    setForm({
      service_category_id: item.service_category_id ?? '',
      name: item.name,
      description: item.description ?? '',
      unit_price: String(item.unit_price ?? ''),
      unit: item.unit ?? '',
      tax_rate_id: item.tax_rate_id ?? '',
      is_active: item.is_active,
    })
    setFormCategoryLabel(
      categoryName(item, t('items.uncategorized')) === t('items.uncategorized')
        ? undefined
        : categoryName(item, t('items.uncategorized'))
    )
    setErrors({})
    setImage(null)
    setImagePreview(item.image_url ?? null)
    setImageRemoved(false)
    setOpen(true)
  }

  function onPickImage(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null
    event.target.value = ''
    if (!file) return

    const imageError = validateSettingsImage(file)
    if (imageError) {
      setErrors(current => ({
        ...current,
        image: t(
          `items.validation.image${imageError === 'type' ? 'Type' : 'Size'}`
        ),
      }))
      return
    }

    setImage(file)
    setImagePreview(URL.createObjectURL(file))
    setImageRemoved(false)
    setErrors(current => ({ ...current, image: '' }))
  }

  function removeImage() {
    setImage(null)
    setImagePreview(null)
    setImageRemoved(Boolean(editing?.image_url))
    setErrors(current => ({ ...current, image: '' }))
  }

  function validate(): ServiceItemFormRequest | null {
    const next: Record<string, string> = {}
    if (!form.service_category_id)
      next.service_category_id = t('items.validation.categoryRequired')
    if (!form.name.trim()) next.name = t('items.validation.nameRequired')
    const price = parseCurrencyInput(form.unit_price)
    if (form.unit_price === '' || price === null || price < 0)
      next.unit_price = t('items.validation.unitPriceRequired')

    setErrors(next)
    if (Object.keys(next).length > 0) return null

    return {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      unit_price: price!,
      unit: form.unit.trim() || undefined,
      service_category_id: form.service_category_id,
      tax_rate_id: form.tax_rate_id || undefined,
      is_active: form.is_active,
      image: image ?? undefined,
      remove_image: editing && imageRemoved ? true : undefined,
    }
  }

  function submit() {
    const body = validate()
    if (!body) return

    const onSuccess = () => {
      toast({
        variant: 'success',
        title: editing ? t('items.toast.updated') : t('items.toast.created'),
      })
      setOpen(false)
    }

    const onError = (error: unknown) =>
      toast({ variant: 'destructive', title: getApiErrorMessage(error) })

    if (editing) {
      update.mutate({ id: editing.id, body }, { onSuccess, onError })
      return
    }

    create.mutate(body, { onSuccess, onError })
  }

  function toggleActive(item: ServiceItem) {
    update.mutate(
      {
        id: item.id,
        body: {
          name: item.name,
          unit_price: item.unit_price,
          is_active: !item.is_active,
        },
      },
      {
        onSuccess: () =>
          toast({
            variant: 'success',
            title: item.is_active
              ? t('items.toast.deactivated')
              : t('items.toast.activated'),
          }),
        onError: error =>
          toast({ variant: 'destructive', title: getApiErrorMessage(error) }),
      }
    )
  }

  function confirmDelete() {
    if (!toDelete) return

    remove.mutate(toDelete.id, {
      onSuccess: () => {
        toast({ variant: 'success', title: t('items.toast.deleted') })
        setToDelete(null)
      },
      onError: error =>
        toast({ variant: 'destructive', title: getApiErrorMessage(error) }),
    })
  }

  return (
    <div className='space-y-5'>
      <div className='grid gap-3 sm:grid-cols-2 xl:grid-cols-4'>
        <SummaryMetric
          label={t('items.summary.articles')}
          value={summary?.articles}
        />
        <SummaryMetric
          label={t('items.summary.categories')}
          value={summary?.categories}
          valueClassName='text-primary'
        />
        <SummaryMetric
          label={t('items.summary.active')}
          value={summary?.active}
          valueClassName='text-[#059669]'
        />
        <SummaryMetric
          label={t('items.summary.inactive')}
          value={summary?.inactive}
        />
      </div>

      <section className={cn(sectionCardClass, 'overflow-visible')}>
        <header className='flex flex-col gap-6 px-6 pb-6 pt-6'>
          <div className='flex items-center justify-between gap-4'>
            <div className='flex min-w-0 flex-1 items-center border-l-4 border-[#ff5a1f] pl-4 py-1'>
              <h2 className='font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
                {t('items.title')}
              </h2>
            </div>
            <Button
              className='h-10 rounded-xl bg-primary px-4 text-sm font-medium leading-none text-white hover:bg-primary/90'
              onClick={openCreate}
            >
              <Plus className='mr-2 size-4' />
              {t('items.addItem')}
            </Button>
          </div>

          <div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
            <div className='relative w-full sm:w-[270px]'>
              <Search className='pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#6c7285]' />
              <Input
                value={search}
                maxLength={100}
                onChange={event => onSearchChange(event.target.value)}
                placeholder={t('items.filters.searchPlaceholder')}
                className={searchInputClass}
              />
            </div>

            <ServiceCategoryFilter
              value={filters.service_category_ids ?? []}
              categories={categoryOptions}
              onChange={ids =>
                setFilters({
                  service_category_ids: ids.length ? ids : undefined,
                })
              }
              className='w-full sm:w-auto [&>button]:text-sm [&>button]:leading-[1.4]'
            />

            <Select
              value={statusValue}
              onValueChange={value =>
                setFilters({
                  is_active:
                    value === STATUS_ALL ? undefined : value === 'active',
                })
              }
            >
              <SelectTrigger className={filterTriggerClass}>
                <SelectValue placeholder={t('items.filters.status')}>
                  {statusValue === STATUS_ALL
                    ? t('items.filters.status')
                    : statusValue === 'active'
                      ? t('common.active')
                      : t('common.inactive')}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={STATUS_ALL}>
                  {t('items.filters.allStatuses')}
                </SelectItem>
                <SelectItem value='active'>{t('common.active')}</SelectItem>
                <SelectItem value='inactive'>{t('common.inactive')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </header>

        <div className='flex flex-col gap-6 bg-white px-4 pb-4 sm:px-6 sm:pb-6'>
          <div className='space-y-[30px]'>
            {isLoading &&
              Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className='space-y-[10px]'>
                  <Skeleton className='h-10 w-full rounded-xl' />
                  <div className='grid gap-[10px] xl:grid-cols-2'>
                    <Skeleton className='h-24 rounded-2xl' />
                    <Skeleton className='h-24 rounded-2xl' />
                  </div>
                </div>
              ))}

            {isError && !isLoading && (
              <div className='rounded-2xl border border-[#f1b4ae] bg-white px-4 py-10 text-center text-sm font-medium text-destructive'>
                {t('items.loadError')}
              </div>
            )}

            {!isLoading && !isError && groups.length === 0 && (
              <div className='rounded-2xl border border-[#e7e9ef] bg-white px-4 py-12 text-center text-sm font-medium text-[#6c7285]'>
                {t('items.empty')}
              </div>
            )}

            {!isLoading &&
              !isError &&
              groups.map(group => (
                <CategorySection
                  key={group.id}
                  group={group}
                  updating={update.isPending}
                  onEdit={openEdit}
                  onToggleActive={toggleActive}
                  onDelete={setToDelete}
                />
              ))}
          </div>

          <ListPagination
            metadata={data?.metadata}
            page={page}
            pageSize={limit}
            pageSizeLabel={t('items.categoriesPerPage')}
            onPageChange={setPage}
            onPageSizeChange={setLimit}
            showSummary={false}
            perPagePosition='left'
          />
        </div>
      </section>

      <Dialog
        open={open}
        onOpenChange={next => {
          if (!next && (create.isPending || update.isPending)) return
          setOpen(next)
        }}
      >
        <DialogContent
          ref={dialogContentRef}
          side='right'
          showCloseButton={false}
          className='tf-right-drawer-short [&>button]:right-6 [&>button]:top-4 [&>button]:rounded-xl [&>button]:bg-[#f4f5f8] [&>button]:p-2 [&>button]:opacity-100 [&>button]:text-[#555b6a]'
        >
          <div className='flex h-full min-h-0 flex-col'>
            <SettingsDrawerHeader
              title={
                editing
                  ? t('items.dialog.editTitle')
                  : t('items.dialog.addTitle')
              }
              closeDisabled={create.isPending || update.isPending}
              titleClassName='text-base'
            />

            <div className='min-h-0 flex-1 overflow-y-auto bg-[#f4f5f8] px-6 py-6'>
              <div
                className={cn(
                  'overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white',
                  cardShadowClass
                )}
              >
                <div className='flex items-center gap-3 px-6 pt-6'>
                  <span className='h-4 w-1 rounded-full bg-primary' />
                  <span className='font-heading text-sm font-semibold lowercase leading-[1.2] text-[#3d434e] first-letter:uppercase'>
                    INFORMATION
                  </span>
                </div>

                <div
                  key={editing?.id ?? 'create'}
                  className='space-y-6 px-6 py-6'
                >
                  <div className='space-y-2'>
                    <FieldLabel>{t('items.fields.image')}</FieldLabel>
                    <input
                      ref={fileInputRef}
                      id='si-image'
                      type='file'
                      accept={SETTINGS_IMAGE_ACCEPT}
                      className='hidden'
                      onChange={onPickImage}
                    />

                    <div className='flex flex-wrap items-center gap-4'>
                      <button
                        type='button'
                        className='flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-[#e7e9ef] bg-[#f4f5f8]'
                        aria-label={t('items.fields.image')}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <ServiceItemImage
                          src={imagePreview}
                          className='size-full rounded-xl bg-[#fff1ea]'
                        />
                      </button>

                      <div className='space-y-1'>
                        <div className='flex flex-wrap items-center gap-3'>
                          <Button
                            type='button'
                            variant='outline'
                            className='h-10 rounded-xl border-dashed border-primary px-4 text-sm font-medium leading-none text-primary hover:bg-primary/5 hover:text-primary'
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <Camera className='mr-2 size-4' />
                            {t('items.fields.addPhoto')}
                          </Button>
                          {imagePreview && (
                            <Button
                              type='button'
                              variant='outline'
                              className='h-10 rounded-xl border-[#b91c1c] px-3 text-sm font-medium leading-none text-[#991b1b] hover:bg-[#fef2f2] hover:text-[#991b1b]'
                              onClick={removeImage}
                            >
                              <Trash2 className='mr-2 size-4' />
                              {t('items.removeImage')}
                            </Button>
                          )}
                        </div>
                        <p className='text-xs font-medium leading-4 text-[#6c7285]'>
                          {t('items.fields.imageHint')}
                        </p>
                        {errors.image && (
                          <p className='text-sm text-destructive'>
                            {errors.image}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className='space-y-2'>
                    <FieldLabel htmlFor='si-name' required>
                      {t('items.fields.name')}
                    </FieldLabel>
                    <Input
                      id='si-name'
                      value={form.name}
                      maxLength={150}
                      placeholder={t('items.fields.enterHere')}
                      onChange={event => {
                        set({ name: event.target.value })
                        setErrors(state => ({ ...state, name: '' }))
                      }}
                      className={cn(
                        sheetInputClass,
                        errors.name && 'border border-destructive bg-white'
                      )}
                    />
                    {errors.name && (
                      <p className='text-sm text-destructive'>{errors.name}</p>
                    )}
                  </div>

                  <div className='space-y-2'>
                    <FieldLabel htmlFor='si-desc'>
                      {t('items.fields.description')}
                    </FieldLabel>
                    <Textarea
                      id='si-desc'
                      value={form.description}
                      maxLength={1000}
                      placeholder={t('items.fields.enterHere')}
                      onChange={event =>
                        set({ description: event.target.value })
                      }
                      className={sheetTextareaClass}
                    />
                  </div>

                  <div className='space-y-2'>
                    <FieldLabel required>
                      {t('items.fields.category')}
                    </FieldLabel>
                    <EntityPicker
                      queryKey='service-categories'
                      fetcher={params =>
                        settingsApi.listServiceCategories({
                          search: params.search,
                          limit: params.limit,
                          page: params.page,
                        })
                      }
                      getOptionLabel={category => category.name}
                      getOptionValue={category => category.id}
                      value={form.service_category_id}
                      initialLabel={formCategoryLabel}
                      onChange={(value, item) => {
                        set({ service_category_id: value })
                        setFormCategoryLabel(item.name)
                        setErrors(current => ({
                          ...current,
                          service_category_id: '',
                        }))
                      }}
                      placeholder={t('items.placeholders.selectCategory')}
                      className={cn(
                        sheetSelectClass,
                        'justify-between border border-transparent',
                        !form.service_category_id && 'text-[#98a2b3]',
                        errors.service_category_id &&
                          'border-destructive bg-white'
                      )}
                      portalContainer={dialogPortalContainer}
                    />
                    {errors.service_category_id && (
                      <p className='text-sm text-destructive'>
                        {errors.service_category_id}
                      </p>
                    )}
                  </div>

                  <div className='grid gap-4 sm:grid-cols-2'>
                    <div className='space-y-2'>
                      <FieldLabel htmlFor='si-price' required>
                        {t('items.fields.unitPrice')}
                      </FieldLabel>
                      <div
                        className={cn(
                          'flex h-11 items-center gap-2 rounded-xl border border-transparent bg-[#f4f5f8] px-4',
                          FORM_CONTROL_FOCUS_WITHIN_CLASS,
                          errors.unit_price && 'border-destructive bg-white'
                        )}
                      >
                        <span className='text-sm font-normal leading-[1.4] text-[#6c7285]'>
                          €
                        </span>
                        <input
                          id='si-price'
                          type='number'
                          step='0.01'
                          min='0'
                          value={form.unit_price}
                          placeholder={t('items.placeholders.unitPrice')}
                          onChange={event => {
                            set({ unit_price: event.target.value })
                            setErrors(state => ({ ...state, unit_price: '' }))
                          }}
                          className='h-full min-w-0 flex-1 bg-transparent text-sm font-normal leading-[1.4] text-[#171a20] outline-none placeholder:text-[#98a2b3]'
                        />
                      </div>
                      {errors.unit_price && (
                        <p className='text-sm text-destructive'>
                          {errors.unit_price}
                        </p>
                      )}
                    </div>

                    <div className='space-y-2'>
                      <FieldLabel htmlFor='si-unit' required>
                        {t('items.fields.unit')}
                      </FieldLabel>
                      <Input
                        id='si-unit'
                        value={form.unit}
                        maxLength={50}
                        placeholder={t('items.placeholders.unit')}
                        onChange={event => set({ unit: event.target.value })}
                        className={sheetInputClass}
                      />
                    </div>
                  </div>

                  <div className='space-y-2'>
                    <FieldLabel>{t('items.fields.taxRate')}</FieldLabel>
                    <Select
                      value={form.tax_rate_id || undefined}
                      onValueChange={value =>
                        set({ tax_rate_id: value === NONE ? '' : value })
                      }
                    >
                      <SelectTrigger
                        className={cn(
                          sheetSelectClass,
                          !form.tax_rate_id && 'text-[#98a2b3]'
                        )}
                      >
                        <SelectValue
                          placeholder={t('items.placeholders.selectTaxRate')}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>
                          {t('items.placeholders.none')}
                        </SelectItem>
                        {taxRates.data?.data.map(rate => (
                          <SelectItem key={rate.id} value={rate.id}>
                            {rate.name} ({rate.rate}%)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className='flex items-center justify-between gap-4'>
                    <Label
                      htmlFor='si-active'
                      className='text-sm font-medium leading-[1.4] text-[#171a20]'
                    >
                      {t('items.fields.active')}
                    </Label>
                    <Switch
                      id='si-active'
                      checked={form.is_active}
                      onCheckedChange={value => set({ is_active: value })}
                      className='mr-2 h-6 w-10 data-[state=checked]:bg-[#10b981] data-[state=unchecked]:bg-[#d9d9d9] [&>span]:h-5 [&>span]:w-5 [&>span]:data-[state=checked]:translate-x-4 [&>span]:data-[state=unchecked]:translate-x-0.5'
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className='tf-right-drawer-footer justify-end'>
              <div className='flex items-center justify-end gap-3'>
                <Button
                  type='button'
                  variant='outline'
                  className='h-10 rounded-xl border-[#e7e9ef] px-4 text-sm font-medium leading-none text-[#171a20]'
                  onClick={() => setOpen(false)}
                >
                  {t('common:actions.cancel')}
                </Button>
                <Button
                  type='button'
                  className='h-10 rounded-xl bg-primary px-4 text-sm font-medium leading-none text-white hover:bg-primary/90'
                  onClick={submit}
                  disabled={create.isPending || update.isPending}
                >
                  {t('common:actions.save')}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!toDelete}
        onOpenChange={isOpen =>
          !remove.isPending && !isOpen && setToDelete(null)
        }
        title={t('items.dialog.deleteTitle')}
        description={t('items.dialog.deleteMessage')}
        confirmLabel={t('items.dialog.deleteConfirm')}
        onConfirm={confirmDelete}
        busy={remove.isPending}
      />
    </div>
  )
}

export default ItemsPanel
